
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

interface ConsultationEvent {
  id: string;
  name: string;
  status: string;
  start_time: string;
  end_time: string;
  location: string;
  meeting_url: string | null;
}

export default function MyConsultationsPage() {
  const [user, setUser] = useState<any>(null);
  const [events, setEvents] = useState<ConsultationEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session?.user) {
        navigate('/auth', { state: { from: { pathname: '/my-consultations' } } });
        return;
      }
      setUser(session.user);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session?.user) {
        navigate('/auth', { state: { from: { pathname: '/my-consultations' } } });
        return;
      }
      setUser(session.user);
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (!user) return;
    fetchConsultations();
  }, [user]);

  const fetchConsultations = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const res = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-my-consultations`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Failed to load consultations');
        setEvents([]);
      } else {
        setEvents(data.events || []);
      }
    } catch (err: any) {
      setError('Unable to connect. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (startStr: string, endStr: string) => {
    const start = new Date(startStr);
    const end = new Date(endStr);
    const opts: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit', hour12: true };
    return `${start.toLocaleTimeString('en-US', opts)} – ${end.toLocaleTimeString('en-US', opts)}`;
  };

  const getTimeUntil = (dateStr: string) => {
    const now = new Date();
    const target = new Date(dateStr);
    const diffMs = target.getTime() - now.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (diffDays > 0) return `In ${diffDays} day${diffDays > 1 ? 's' : ''}`;
    if (diffHours > 0) return `In ${diffHours} hour${diffHours > 1 ? 's' : ''}`;
    return 'Starting soon';
  };

  const isToday = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    return date.toDateString() === now.toDateString();
  };

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Navbar */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 cursor-pointer">
              <img
                src="https://static.readdy.ai/image/ff0b7e80ce034da2f0ffbed7fce4b50d/d817fcc14e7e8fe425753ab5de5ab717.jpeg"
                alt="Vazcular Logo"
                className="h-10 w-auto"
              />
              <span className="text-xl font-semibold text-gray-900">Vazcular</span>
            </Link>
            <div className="flex items-center gap-4">
              {user && (
                <span className="text-sm text-gray-600 hidden md:block">
                  <i className="ri-user-line mr-1"></i>
                  {user.user_metadata?.full_name || user.email}
                </span>
              )}
              <Link
                to="/"
                className="text-gray-600 hover:text-[#f04e58] transition-colors text-sm flex items-center gap-2 cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="bg-gradient-to-br from-[#fef5f5] to-white py-12">
        <div className="max-w-4xl mx-auto px-6">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">My Consultations</h1>
          <p className="text-gray-600 text-sm">Your upcoming scheduled appointments</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-10">
        <div className="max-w-4xl mx-auto px-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-10 h-10 border-4 border-[#f04e58]/20 border-t-[#f04e58] rounded-full animate-spin mb-4"></div>
              <p className="text-gray-500 text-sm">Loading your consultations...</p>
            </div>
          ) : error ? (
            <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-error-warning-line text-3xl text-red-400"></i>
              </div>
              <p className="text-gray-700 font-medium mb-2">Something went wrong</p>
              <p className="text-gray-500 text-sm mb-6">{error}</p>
              <button
                onClick={fetchConsultations}
                className="inline-flex items-center gap-2 px-5 py-2 bg-[#f04e58] text-white text-sm font-medium rounded-lg hover:bg-[#d93d47] transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>
                Try Again
              </button>
            </div>
          ) : events.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
              <div className="w-16 h-16 bg-[#fef5f5] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-calendar-line text-3xl text-[#f04e58]"></i>
              </div>
              <p className="text-gray-900 font-semibold text-lg mb-2">No upcoming consultations</p>
              <p className="text-gray-500 text-sm mb-6">
                You don&apos;t have any scheduled appointments yet.
              </p>
              <Link
                to="/consultation"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#f04e58] text-white text-sm font-medium rounded-lg hover:bg-[#d93d47] transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line"></i>
                Book a Consultation
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {events.map((event) => (
                <div
                  key={event.id}
                  className={`bg-white rounded-xl border transition-all hover:shadow-md ${
                    isToday(event.start_time)
                      ? 'border-[#f04e58]/30 shadow-sm'
                      : 'border-gray-200'
                  }`}
                >
                  <div className="p-6">
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      {/* Left: Info */}
                      <div className="flex gap-4">
                        <div
                          className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            isToday(event.start_time) ? 'bg-[#f04e58] text-white' : 'bg-[#fef5f5] text-[#f04e58]'
                          }`}
                        >
                          <i className="ri-calendar-check-line text-xl"></i>
                        </div>
                        <div>
                          <h3 className="text-base font-semibold text-gray-900 mb-1">{event.name}</h3>
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
                            <span className="flex items-center gap-1.5">
                              <i className="ri-calendar-line text-gray-400"></i>
                              {formatDate(event.start_time)}
                            </span>
                            <span className="flex items-center gap-1.5">
                              <i className="ri-time-line text-gray-400"></i>
                              {formatTime(event.start_time, event.end_time)}
                            </span>
                          </div>
                          {event.location && event.location !== 'To be confirmed' && (
                            <p className="text-sm text-gray-500 mt-1 flex items-center gap-1.5">
                              <i className="ri-map-pin-line text-gray-400"></i>
                              {event.location}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Right: Badge + Action */}
                      <div className="flex items-center gap-3 md:flex-shrink-0">
                        <span
                          className={`text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap ${
                            isToday(event.start_time)
                              ? 'bg-[#f04e58] text-white'
                              : 'bg-[#fef5f5] text-[#f04e58]'
                          }`}
                        >
                          {isToday(event.start_time) ? 'Today' : getTimeUntil(event.start_time)}
                        </span>
                        {event.meeting_url && (
                          <a
                            href={event.meeting_url}
                            target="_blank"
                            rel="noopener noreferrer nofollow"
                            className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#f04e58] text-white text-sm font-medium rounded-lg hover:bg-[#d93d47] transition-all cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-video-chat-line"></i>
                            Join
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Book more */}
              <div className="pt-4 text-center">
                <Link
                  to="/consultation"
                  className="inline-flex items-center gap-2 text-sm text-[#f04e58] hover:underline cursor-pointer font-medium"
                >
                  <i className="ri-add-circle-line"></i>
                  Book another consultation
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
